// src/services/billingService.js
/**
 * @file billingService.js
 * @description Service for managing billing-related data using the Financials Usage API.
 */
import api from "./apiClient";

const CACHE = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 Minutes
const API_URL = "https://utility.wiiz.it/api/admin/financials-usage/dashboard";

/**
 * Retrieves billing overview data with caching.
 * 
 * @param {string} timePeriod - Time period for billing metrics (e.g. 'monthly', 'weekly', 'all_time').
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Billing overview data mapped for the frontend.
 */
export async function fetchBillingOverview(timePeriod = "monthly", customRange = null, planFilter = null, forceRefresh = false) {
  const cacheKey = `BILLING_${timePeriod}_${planFilter || 'all'}_${customRange ? JSON.stringify(customRange) : ''}`;
  const cachedEntry = CACHE.get(cacheKey);
  const now = Date.now();

  // 1. Check Cache
  if (!forceRefresh && cachedEntry && now - cachedEntry.timestamp < CACHE_TTL) {
    console.log("⚡ Returning Cached Billing Data for:", cacheKey);
    return cachedEntry.data;
  }

  try {
    console.log(`fetching billing data for period: ${timePeriod}`);
    const payload = {};

    if (planFilter) {
      payload.plan_filter = planFilter;
    }

    if (timePeriod === "custom") {
      if (customRange?.start && customRange?.end) {
        payload.start_date = customRange.start;
        payload.end_date = customRange.end;
      } else {
        console.warn("Skipping Billing API call: Custom range incomplete");
        return null;
      }
    } else {
      payload.time_period = timePeriod;
    }
    const response = await api.post(API_URL, payload);

    if (response.data && response.data.status === "success") {
      const apiData = response.data.data;

      // Map API Response to Frontend Model
      const mappedData = {
        metrics: {
          totalMRR: parseFloat((apiData.summary_kpis?.mrr || "0").replace(/[^0-9.-]+/g, "")),
          totalRevenue: 0, // Not explicitly in summary_kpis
          risk: `${apiData.summary_kpis?.risk_indicator || 0} Subscribers`,
          avgUsage: apiData.summary_kpis?.avg_usage_tenant || "0%"
        },
        revenueTrend: {
          labels: apiData.revenue_trend?.map(item => item.date) || [],
          series: [{
            name: "Revenue ($)",
            data: apiData.revenue_trend?.map(item => item.revenue) || []
          }]
        },
        planDistribution: {
          labels: apiData.revenue_by_plan?.map(p => p.plan) || [],
          series: apiData.revenue_by_plan?.map(p => p.revenue) || []
        },
        topTenants: apiData.top_consumers?.map(tenant => ({
          name: tenant.tenant,
          plan: tenant.plan,
          revenue: 0,
          executions: tenant.executions_used,
          storage: `${((tenant.storage_used || 0) * 1024).toFixed(2)} MB`,
          globalPct: `${tenant.usage_percentage?.toFixed(1) || 0}%`
        })) || []
      };

      // Cache result
      CACHE.set(cacheKey, {
        data: mappedData,
        timestamp: Date.now()
      });

      return mappedData;
    }
  } catch (error) {
    console.error("Billing API Error:", error);
  }

  return null;
}

/**
 * Retrieves billing details for a specific tenant.
 * 
 * @param {string} tenantId - The unique ID of the tenant.
 * @returns {Promise<Object>} Tenant billing information.
 */
export async function getBillingForTenant(tenantId) {
  try {
    const url = `https://utility.wiiz.it/api/admin/financials-usage/tenant/${tenantId}/details`;
    const res = await api.get(url);
    if (res.data && res.data.status === "success") {
      const tData = res.data.data;
      return {
        plan: tData.tenant_info?.plan,
        monthlyCost: 0,
        usage: tData.execution_usage?.used,
        status: "Active",
        overages: 0,
        nextBillingDate: "-"
      };
    }
  } catch (error) {
    console.warn("Billing Tenant API unavailable/error:", error);
  }

  // Fallback mock if API fails
  return {
    plan: "Basic",
    monthlyCost: 4.9,
    usage: 1750,
    status: "Paid",
    overages: 0,
    nextBillingDate: "2024-02-01",
  };
}